import { useState } from "react";
import { ChevronLeft, ChevronRight, Inbox, Plus, RefreshCw, Truck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/auth/UserMenu";

const WeekNav = ({ label, onPrev, onNext, onToday }) => (
  <div className="flex w-full items-center gap-1 sm:w-auto">
    <Button data-testid="week-prev-btn" variant="ghost" size="icon" className="h-10 w-10 rounded-xl" onClick={onPrev} aria-label="Settimana precedente">
      <ChevronLeft className="h-5 w-5" />
    </Button>
    <span data-testid="week-label" className="flex-1 text-center font-display text-sm font-semibold sm:min-w-[170px]">{label}</span>
    <Button data-testid="week-next-btn" variant="ghost" size="icon" className="h-10 w-10 rounded-xl" onClick={onNext} aria-label="Settimana successiva">
      <ChevronRight className="h-5 w-5" />
    </Button>
    <Button data-testid="week-today-btn" variant="outline" className="h-10 rounded-xl" onClick={onToday}>Oggi</Button>
  </div>
);

const ViewTabs = ({ view, setView, openCount }) => (
  <div className="view-tabs w-full sm:w-auto" role="tablist">
    {[["grid", "Griglia settimanale"], ["open", `Attività aperte (${openCount})`]].map(([key, label]) => (
      <button
        key={key}
        type="button"
        role="tab"
        data-testid={`view-tab-${key}`}
        aria-selected={view === key}
        onClick={() => setView(key)}
        className={view === key ? "is-active" : ""}
      >
        {label}
      </button>
    ))}
  </div>
);

const RefreshButton = ({ onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    await onRefresh();
    setBusy(false);
  };
  return (
    <Button data-testid="refresh-btn" variant="outline" className="h-10 gap-2 rounded-xl px-2.5 sm:px-4" onClick={run} disabled={busy} aria-label="Aggiorna" title="Aggiorna">
      <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
      <span className="hidden lg:inline">Aggiorna</span>
    </Button>
  );
};

export const Header = ({ week, view, setView, openCount, unassignedCount, showUnassignedBtn, onUnassigned, onEmployees, onVehicles, onNew, onRefresh }) => (
  <header className="app-header sticky top-0 z-40">
    <div className="mx-auto max-w-[1600px] space-y-3 px-3 py-3 sm:px-6">
      <div className="flex items-center gap-3">
        <img src="/images/logo-raf.png" alt="R.A.F. ECOLOGIA" data-testid="header-logo" className="h-8 w-auto shrink-0 sm:h-12" />
        <div className="hidden min-w-0 border-l border-slate-200 pl-3 md:block">
          <h1 className="truncate font-display text-base font-bold leading-tight tracking-tight lg:text-lg"><span className="sr-only">R.A.F. ECOLOGIA · </span>Planner settimanale</h1>
          <p className="text-xs text-slate-500">Attività e turni dei dipendenti</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          {showUnassignedBtn && (
            <Button data-testid="open-unassigned-btn" variant="outline" className="h-10 gap-1.5 rounded-xl px-2.5 sm:gap-2 sm:px-4" onClick={onUnassigned}>
              <Inbox className="h-4 w-4" />
              <span className="hidden sm:inline">Da assegnare</span>
              <span className="count-pill">{unassignedCount}</span>
            </Button>
          )}
          <RefreshButton onRefresh={onRefresh} />
          <Button data-testid="manage-vehicles-btn" variant="outline" className="h-10 gap-2 rounded-xl px-2.5 sm:px-4" onClick={onVehicles} aria-label="Gestisci automezzi">
            <Truck className="h-4 w-4" />
            <span className="hidden sm:inline">Automezzi</span>
          </Button>
          <Button data-testid="manage-employees-btn" variant="outline" className="h-10 gap-2 rounded-xl px-2.5 sm:px-4" onClick={onEmployees} aria-label="Gestisci dipendenti">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Dipendenti</span>
          </Button>
          <Button data-testid="new-task-btn" className="hidden h-10 gap-2 rounded-xl md:inline-flex" onClick={onNew}>
            <Plus className="h-4 w-4" /> Nuova attività
          </Button>
          <UserMenu />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <ViewTabs view={view} setView={setView} openCount={openCount} />
        {view === "grid" && (
          <div className="sm:ml-auto w-full sm:w-auto">
            <WeekNav {...week} />
          </div>
        )}
      </div>
    </div>
  </header>
);
